<template>
    <LandingPageLayout
        v-bind="landingPageSharedData.getLandingPageLayoutProps()"
    >
        <FeaturePageHeroSection
            title="Payments built into your booking flow"
            subtitle="Collect payment during scheduling, manage refunds, and keep payment policy consistent across discovery calls, group sessions, and recurring offers."
            buttonLabel="Enable booking payments"
            :buttonUrl="heroRegisterUrl"
        />

        <section class="border-b border-slate-200 bg-slate-50">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <div class="grid gap-10 lg:grid-cols-2 lg:items-start">
                    <div>
                        <FeaturePageSectionHeader
                            title="Payment coverage across all meeting formats"
                            subtitle="Use one payment strategy across your offers with built-in refund support."
                            subtitleClass="mt-3 text-slate-600"
                        />

                        <FeaturePagePrimaryButton
                            class="mt-7"
                            label="Enable booking payments"
                            :href="ctaRegisterUrl"
                        />
                    </div>

                    <div>
                        <FeaturePageMockPanel
                            title="Payment Operations"
                            badgeText="Policy ready"
                        >
                            <FeaturePageInfoCard
                                v-for="paymentOperation in paymentOperations"
                                :key="paymentOperation.title"
                                :title="paymentOperation.title"
                                :description="paymentOperation.description"
                                containerClass="rounded-xl border border-slate-200 bg-slate-50 p-4"
                                titleClass="text-sm font-bold"
                                descriptionClass="mt-1 text-sm text-slate-600"
                            />
                        </FeaturePageMockPanel>
                    </div>
                </div>
            </div>
        </section>

        <section class="border-b border-slate-200 bg-white">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <FeaturePageSectionHeader
                    title="Payment capability breakdown"
                    subtitle="Apply one payment strategy across service formats and keep refund handling predictable."
                />

                <div class="mt-8 grid gap-4 md:grid-cols-3">
                    <FeaturePageInfoCard
                        v-for="paymentCapability in paymentCapabilities"
                        :key="paymentCapability.title"
                        :title="paymentCapability.title"
                        :description="paymentCapability.description"
                        containerClass="rounded-xl border border-slate-200 bg-slate-50 p-4"
                        :badgeText="
                            paymentCapability.isPlanned ? 'Planned' : null
                        "
                        badgeVariant="upcoming"
                        badgeSize="xs"
                        badgePosition="bottom"
                        badgeClass="mt-2"
                    />
                </div>
            </div>
        </section>

        <section class="border-b border-slate-200 bg-slate-50">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <FeaturePageBadge
                    text="Planned"
                    variant="upcoming"
                    uppercase
                />

                <FeaturePageSectionHeader
                    containerClass="mt-4"
                    title="Discount coupons"
                    subtitle="Configure coupon rules for promotions and controlled discount campaigns without losing payment guardrails."
                />

                <div class="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <FeaturePageInfoCard
                        v-for="couponCapability in couponCapabilities"
                        :key="couponCapability.title"
                        :title="couponCapability.title"
                        :description="couponCapability.description"
                        containerClass="rounded-xl border border-slate-200 bg-white p-4"
                    />
                </div>
            </div>
        </section>

        <section class="bg-white">
            <div class="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
                <FeaturePageSectionHeader
                    title="Sample use cases"
                    subtitle="Payment support helps solo experts charge confidently across different offer types and cancellation policies."
                />

                <div class="mt-8 grid gap-4 md:grid-cols-3">
                    <FeaturePageInfoCard
                        v-for="sampleUseCase in sampleUseCases"
                        :key="sampleUseCase.title"
                        :title="sampleUseCase.title"
                        :description="sampleUseCase.description"
                        containerClass="rounded-xl border border-slate-200 bg-slate-50 p-5"
                        titleClass="text-lg font-bold"
                        descriptionClass="mt-2 text-sm text-slate-600"
                    />
                </div>
            </div>
        </section>
    </LandingPageLayout>
</template>

<script setup lang="ts">
    import { UtmContentEnum } from '@/Shared/Service/Tracking/Utm/UtmEnums'
    import FeaturePageBadge from '@/Marketing/LandingPage/Component/FeaturePage/Badge.vue'
    import FeaturePageHeroSection from '@/Marketing/LandingPage/Component/FeaturePage/HeroSection.vue'
    import FeaturePageInfoCard from '@/Marketing/LandingPage/Component/FeaturePage/InfoCard.vue'
    import FeaturePageMockPanel from '@/Marketing/LandingPage/Component/FeaturePage/MockPanel.vue'
    import FeaturePagePrimaryButton from '@/Marketing/LandingPage/Component/FeaturePage/PrimaryButton.vue'
    import FeaturePageSectionHeader from '@/Marketing/LandingPage/Component/FeaturePage/SectionHeader.vue'
    import LandingPageSharedData from '@/Marketing/LandingPage/Entity/LandingPageSharedData'
    import LandingPageLayout from '@/Marketing/LandingPage/Shared/LandingPageLayout.vue'
    import { UrlFactory } from '@/Shared/Url/UrlFactory'

    interface LandingPageProps {
        landingPageSharedDataRawData: any
    }

    interface PaymentCapability {
        title: string
        description: string
        isPlanned: boolean
    }

    interface PaymentOperation {
        title: string
        description: string
    }

    interface CouponCapability {
        title: string
        description: string
    }

    interface SampleUseCase {
        title: string
        description: string
    }

    const props = defineProps<LandingPageProps>()

    const landingPageSharedData = LandingPageSharedData.hydrate(
        props.landingPageSharedDataRawData,
    )
    const urlFactory = new UrlFactory()
    const heroRegisterUrl = urlFactory.makeLandingPageRegisterUrl(
        UtmContentEnum.PaymentsHero,
    )
    const ctaRegisterUrl = urlFactory.makeLandingPageRegisterUrl(
        UtmContentEnum.PaymentsCta,
    )

    const paymentCapabilities: PaymentCapability[] = [
        {
            title: 'Service payment coverage',
            description:
                'Supports 1:1 meetings, group meetings, and recurring meetings with one consistent booking payment flow.',
            isPlanned: false,
        },
        {
            title: 'Manual refunds',
            description:
                'Admins can issue refunds directly when policy exceptions or service issues happen.',
            isPlanned: false,
        },
        {
            title: 'Automatic refunds on cancellation',
            description:
                'Optionally refund payments automatically when clients cancel sessions.',
            isPlanned: false,
        },
        {
            title: 'Stripe',
            description:
                'Stripe is supported for secure payment processing inside the booking flow.',
            isPlanned: false,
        },
        {
            title: 'PayPal',
            description:
                'PayPal support is planned as an additional provider for payment collection.',
            isPlanned: true,
        },
    ]

    const couponCapabilities: CouponCapability[] = [
        {
            title: 'Fixed amount discounts',
            description:
                'Create coupons with a flat amount reduction to keep campaign value predictable.',
        },
        {
            title: 'Percentage discounts',
            description:
                'Use percentage-based coupons for flexible promotions across different price points.',
        },
        {
            title: 'Customer limits',
            description:
                'Restrict coupon usage to specific clients or client-level eligibility rules.',
        },
        {
            title: 'Redemption limits',
            description:
                'Set maximum redemption counts to avoid unlimited or uncontrolled coupon usage.',
        },
        {
            title: 'Service-level targeting',
            description:
                'Limit coupons to specific services so discounts apply only where intended.',
        },
        {
            title: 'Composable rule set',
            description:
                'Combine multiple limits and targeting rules to model real campaign constraints.',
        },
    ]

    const paymentOperations: PaymentOperation[] = [
        {
            title: 'Collection policy',
            description:
                'Apply one consistent payment rule across 1:1, group, and recurring services.',
        },
        {
            title: 'Refund controls',
            description:
                'Manual refunds and automatic refunds on cancellation are both available at all times.',
        },
        {
            title: 'Provider strategy',
            description:
                'Run Stripe today and expand to PayPal once planned support becomes available.',
        },
    ]

    const sampleUseCases: SampleUseCase[] = [
        {
            title: 'Paid discovery calls',
            description:
                'Charge before booking so advisors spend time only on confirmed and paid consultations.',
        },
        {
            title: 'Workshop enrollment',
            description:
                'Collect payment per attendee and issue refunds cleanly when cancellation policies apply.',
        },
        {
            title: 'Recurring coaching plans',
            description:
                'Combine recurring booking with upfront payment and consistent refund handling.',
        },
    ]
</script>
